<app-page-shell>
  <app-page-hero
    eyebrow="Transport Planning"
    title="Add Work For Trucks"
    subtitle="Add jobs to the planning pool. This does not create a shipment yet.">
    <div actions>
      <p-button label="Back To Releases" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/transport-planning/releases"></p-button>
    </div>
  </app-page-hero>

  @if (message) {
    <div class="notice">
      <i class="pi pi-info-circle"></i>
      <span>{{ message }}</span>
    </div>
  }

  <section class="quick-flow" aria-label="Release workflow">
    <div class="flow-step">
      <span class="flow-icon source"><i class="pi pi-inbox"></i></span>
      <div>
        <strong>Choose source</strong>
        <span>Manual entry</span>
      </div>
    </div>
    <div class="flow-step">
      <span class="flow-icon route"><i class="pi pi-map-marker"></i></span>
      <div>
        <strong>Set route</strong>
        <span>Start and end places</span>
      </div>
    </div>
    <div class="flow-step">
      <span class="flow-icon time"><i class="pi pi-calendar-clock"></i></span>
      <div>
        <strong>Add window</strong>
        <span>Pickup and delivery dates</span>
      </div>
    </div>
    <div class="flow-step">
      <span class="flow-icon pool"><i class="pi pi-list-check"></i></span>
      <div>
        <strong>Plan later</strong>
        <span>Ready for truck planning</span>
      </div>
    </div>
  </section>

  <app-page-data-panel title="Manual Release" subtitle="One pickup, one drop-off, one load.">
          <div class="release-entry">
            <aside class="entry-side manual-side">
              <div class="entry-icon"><i class="pi pi-pencil"></i></div>
              <h3>Manual entry</h3>
              <p>Work that is not already linked to an order.</p>
            </aside>

            <div class="form-stack">
              <section class="form-section">
                <div class="section-title">
                  <span>1</span>
                  <div>
                    <h3>Customer</h3>
                    <p>The complete CRM-first commercial party is copied into the generated shipment.</p>
                  </div>
                </div>
                <div class="form-grid">
                  <app-commercial-party-picker class="field commercial-party-field"
                    [owningCompanyId]="companyId()"
                    [value]="commercialPartyReference"
                    [required]="true"
                    label="Shipment Commercial Party"
                    (valueChange)="onCommercialPartyChange($event)">
                  </app-commercial-party-picker>
                  <p-inputgroup class="field">
                    <p-inputgroup-addon><i class="pi pi-briefcase"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <app-logistics-project-lookup inputId="manualLogisticsProject" [(ngModel)]="selectedLogisticsProjectId" placeholder="Choose logistics project"></app-logistics-project-lookup>
                      <label for="manualLogisticsProject">Logistics project</label>
                    </p-iftalabel>
                  </p-inputgroup>
                </div>
              </section>

              <section class="form-section">
                <div class="section-title">
                  <span>2</span>
                  <div>
                    <h3>Route</h3>
                    <p>Saved places with coordinates.</p>
                  </div>
                </div>
                <div class="form-grid">
                  <p-inputgroup class="field">
                    <p-inputgroup-addon><i class="pi pi-home"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-select inputId="manualStartLocation" appLazyLocationPicker [(ngModel)]="startLocation" optionLabel="name" [filter]="true" filterBy="name,address.shortAddress" appendTo="body" placeholder="Choose start place" styleClass="w-full"></p-select>
                      <label for="manualStartLocation">Truck starts from</label>
                    </p-iftalabel>
                    <p-inputgroup-addon class="quick-create-addon">
                      <p-button icon="pi pi-plus" [text]="true" [rounded]="true" ariaLabel="Create source location" pTooltip="Create source location" tooltipPosition="top" (onClick)="openLocationQuickCreate('source')"></p-button>
                    </p-inputgroup-addon>
                  </p-inputgroup>
                  <p-inputgroup class="field">
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-select inputId="manualEndLocation" appLazyLocationPicker [(ngModel)]="endLocation" optionLabel="name" [filter]="true" appendTo="body" placeholder="Choose end place" styleClass="w-full"></p-select>
                      <label for="manualEndLocation">Truck goes to</label>
                    </p-iftalabel>
                    <p-inputgroup-addon class="quick-create-addon">
                      <p-button icon="pi pi-plus" [text]="true" [rounded]="true" ariaLabel="Create destination location" pTooltip="Create destination location" tooltipPosition="top" (onClick)="openLocationQuickCreate('destination')"></p-button>
                    </p-inputgroup-addon>
                  </p-inputgroup>
                </div>
              </section>

              <section class="form-section">
                <div class="section-title">
                  <span>3</span>
                  <div>
                    <h3>Time window</h3>
                    <p>Pickup and delivery commitment.</p>
                  </div>
                </div>
                <div class="form-grid">
                  <p-inputgroup class="field">
                    <p-inputgroup-addon><i class="pi pi-calendar-plus"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-datepicker inputId="manualPickupDate" [(ngModel)]="pickupDate" [showTime]="true" hourFormat="12" [showIcon]="true" [showButtonBar]="true" appendTo="body" styleClass="w-full"></p-datepicker>
                      <label for="manualPickupDate">Pickup date</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup class="field">
                    <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-datepicker inputId="manualDeliveryDate" [(ngModel)]="deliveryDate" [showTime]="true" hourFormat="12" [showIcon]="true" [showButtonBar]="true" appendTo="body" styleClass="w-full"></p-datepicker>
                      <label for="manualDeliveryDate">Deliver by</label>
                    </p-iftalabel>
                  </p-inputgroup>
                </div>
              </section>

              <section class="form-section">
                <div class="section-title">
                  <span>4</span>
                  <div>
                    <h3>Load</h3>
                    <p>Details for dispatch.</p>
                  </div>
                </div>
                <div class="form-grid">
                  <p-inputgroup class="field wide">
                    <p-inputgroup-addon><i class="pi pi-qrcode"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <input pInputText id="manualCustomerTrackingNumber" [(ngModel)]="customerTrackingNumber" placeholder="Leave blank to auto-generate">
                      <label for="manualCustomerTrackingNumber">Customer tracking number</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup class="field wide">
                    <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-select inputId="manualPayloadType" [options]="payloadTypeOptions" [(ngModel)]="payloadTypeId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" placeholder="Choose shipment payload type" styleClass="w-full"></p-select>
                      <label for="manualPayloadType">Payload type fallback</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup class="field wide">
                    <p-inputgroup-addon><i class="pi pi-warehouse"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-select inputId="manualInventoryItem" [options]="warehousingItems" [(ngModel)]="selectedInventoryItemId" optionValue="id" [filter]="true" filterBy="itemNumber,skuNumber,itemName,itemDescription" [showClear]="true" appendTo="body" placeholder="Choose exact warehouse item" styleClass="w-full">
                        <ng-template #selectedItem let-item>
                          <span>{{ warehousingItemLabel(item) }}</span>
                        </ng-template>
                        <ng-template #item let-item>
                          <span>{{ warehousingItemLabel(item) }}</span>
                        </ng-template>
                      </p-select>
                      <label for="manualInventoryItem">Warehouse inventory item</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  @if (selectedInventoryItemId) {
                    <p-inputgroup class="field">
                      <p-inputgroup-addon><i class="pi pi-building"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-select inputId="manualSourceWarehouse" [options]="warehouses" [(ngModel)]="selectedSourceWarehouseId" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code" [showClear]="true" appendTo="body" styleClass="w-full" (onChange)="onSourceWarehouseChange()"></p-select>
                        <label for="manualSourceWarehouse">Source warehouse</label>
                      </p-iftalabel>
                    </p-inputgroup>
                    <p-inputgroup class="field">
                      <p-inputgroup-addon><i class="pi pi-th-large"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-select inputId="manualSourceZone" [options]="zoneOptionsForSelectedWarehouse()" [(ngModel)]="selectedSourceZoneId" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="manualSourceZone">Source zone optional</label>
                      </p-iftalabel>
                    </p-inputgroup>
                    <p-inputgroup class="field">
                      <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-select inputId="manualCondition" [options]="conditions" [(ngModel)]="selectedConditionId" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="manualCondition">Item condition</label>
                      </p-iftalabel>
                    </p-inputgroup>
                  }
                  <p-inputgroup class="field">
                    <p-inputgroup-addon><i class="pi pi-sort-numeric-up"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-inputNumber inputId="manualUnits" [(ngModel)]="units" [min]="1" [showButtons]="true" styleClass="w-full"></p-inputNumber>
                      <label for="manualUnits">Units</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup class="field">
                    <p-inputgroup-addon><i class="pi pi-tag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-select inputId="manualUnitOfMeasure" [options]="uomOptions" [(ngModel)]="unitOfMeasure" optionLabel="label" optionValue="value" [filter]="true" [editable]="true" appendTo="body" placeholder="Choose or type UOM" styleClass="w-full"></p-select>
                      <label for="manualUnitOfMeasure">Unit of measure</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup class="field">
                    <p-inputgroup-addon><i class="pi pi-clone"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-select inputId="manualSplitPolicy" [options]="splitPolicyOptions" [(ngModel)]="splitPolicy" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                      <label for="manualSplitPolicy">Planning split policy</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  @if (splitPolicy !== 'NONE') {
                    <p-inputgroup class="field">
                      <p-inputgroup-addon><i class="pi pi-copy"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-inputNumber inputId="manualMaximumSplits" [(ngModel)]="maximumSplits" [min]="2" [max]="20" [showButtons]="true" styleClass="w-full"></p-inputNumber>
                        <label for="manualMaximumSplits">Maximum splits</label>
                      </p-iftalabel>
                    </p-inputgroup>
                  }
                  @if (splitPolicy === 'QUANTITY') {
                    <p-inputgroup class="field">
                      <p-inputgroup-addon><i class="pi pi-sort-amount-up"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-inputNumber inputId="manualMinimumSplitQuantity" [(ngModel)]="minimumSplitQuantity" [min]="0.01" [minFractionDigits]="0" [maxFractionDigits]="3" styleClass="w-full"></p-inputNumber>
                        <label for="manualMinimumSplitQuantity">Minimum split quantity</label>
                      </p-iftalabel>
                    </p-inputgroup>
                  }
                </div>
              </section>
            </div>
          </div>

          <div class="actions">
            <p-button label="Add To Planning Pool" icon="pi pi-check" [loading]="isLoading" (onClick)="saveManual()"></p-button>
          </div>
  </app-page-data-panel>

  <app-party-quick-create-dialog
    [visible]="showClientQuickCreateDialog"
    (visibleChange)="showClientQuickCreateDialog = $event"
    mode="customer"
    [record]="clientQuickCreateDraft"
    [countries]="countries"
    [owningCompany]="owningCompany()"
    [defaultCountryId]="owningCompany()?.countryId || null"
    [companyRequired]="false"
    titleOverride="Add CRM Client"
    submitLabel="Create CRM Client"
    (created)="onClientQuickCreated($event)"
    (failed)="onQuickCreateFailed($event)">
  </app-party-quick-create-dialog>

  <app-party-quick-create-dialog
    [visible]="showContactQuickCreateDialog"
    (visibleChange)="showContactQuickCreateDialog = $event"
    mode="customer"
    [record]="contactQuickCreateDraft"
    [countries]="countries"
    [owningCompany]="owningCompany()"
    [defaultCountryId]="owningCompany()?.countryId || null"
    [companyRequired]="false"
    titleOverride="Add CRM Contact"
    submitLabel="Save Contact"
    (created)="onContactQuickCreated($event)"
    (failed)="onQuickCreateFailed($event)">
  </app-party-quick-create-dialog>

  <app-party-quick-create-dialog
    [visible]="showSourceLocationQuickCreateDialog"
    (visibleChange)="showSourceLocationQuickCreateDialog = $event"
    mode="location"
    [record]="sourceLocationQuickCreateDraft"
    [countries]="countries"
    [owningCompany]="owningCompany()"
    [defaultCountryId]="owningCompany()?.countryId || null"
    titleOverride="Add Source Location"
    submitLabel="Create Source Location"
    (created)="onLocationQuickCreated('source', $event)"
    (failed)="onQuickCreateFailed($event)">
  </app-party-quick-create-dialog>

  <app-party-quick-create-dialog
    [visible]="showDestinationLocationQuickCreateDialog"
    (visibleChange)="showDestinationLocationQuickCreateDialog = $event"
    mode="location"
    [record]="destinationLocationQuickCreateDraft"
    [countries]="countries"
    [owningCompany]="owningCompany()"
    [defaultCountryId]="owningCompany()?.countryId || null"
    titleOverride="Add Destination Location"
    submitLabel="Create Destination Location"
    (created)="onLocationQuickCreated('destination', $event)"
    (failed)="onQuickCreateFailed($event)">
  </app-party-quick-create-dialog>
</app-page-shell>
